






















































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
  TOTAL_PANEL_MOBILE_BREAKPOINT,
  TOTAL_PANEL_ROUTES,
} from "@/constants";

interface TotalItem {
  id: number;
  label: string;
  value: number;
}

@Component
export default class SelectedTotalPanel extends Vue {
  @Prop({ default: false }) private mobileVisible!: boolean;

  private items: TotalItem[] = [];
  private collapsed: boolean = false;
  private nextId: number = 1;
  private x: number = 0;
  private y: number = 0;
  private dragging: boolean = false;
  private dragOffsetX: number = 0;
  private dragOffsetY: number = 0;
  private activePointerId: number | null = null;
  private windowWidth: number = typeof window !== "undefined" ? window.innerWidth : TOTAL_PANEL_MOBILE_BREAKPOINT;

  get totalSum(): number {
    return this.items.reduce((sum, item) => sum + item.value, 0);
  }

  get hasItems(): boolean {
    return this.items.length > 0;
  }

  get panelStyle(): object {
    return {
      left: this.x + "px",
      top: this.y + "px",
    };
  }

  get isOnPanelRoute(): boolean {
    return TOTAL_PANEL_ROUTES.includes(this.$route.name as string);
  }

  get isMobileViewport(): boolean {
    return this.windowWidth < TOTAL_PANEL_MOBILE_BREAKPOINT;
  }

  get shouldShowPanel(): boolean {
    return this.isOnPanelRoute && (!this.isMobileViewport || this.mobileVisible);
  }

  @Watch("$route")
  onRouteChange() {
    this.endDragState();
    this.reset();
    this.setMobileVisible(false);
    this.setDefaultPosition();
  }

  created() {
    this.$bus.$on("total-panel:add", this.addItem);
    this.$bus.$on("total-panel:reset", this.reset);
  }

  mounted() {
    this.updateViewportWidth();
    this.setDefaultPosition();
    window.addEventListener("resize", this.handleResize);
  }

  beforeDestroy() {
    this.endDragState();
    this.$bus.$off("total-panel:add", this.addItem);
    this.$bus.$off("total-panel:reset", this.reset);
    window.removeEventListener("resize", this.handleResize);
  }

  addItem = (payload: { label: string; value: number }) => {
    const wasMobileVisible = this.mobileVisible;

    this.items.push({
      id: this.nextId++,
      label: payload.label,
      value: Number(payload.value) || 0,
    });

    this.collapsed = false;

    if (this.isMobileViewport) {
      this.setMobileVisible(true);

      if (!wasMobileVisible) {
        this.$nextTick(() => {
          this.setDefaultPosition();
        });
      }
    }

    this.$nextTick(() => {
      this.$forceUpdate();
    });
  };

  removeItem(id: number) {
    const itemIndex = this.items.findIndex((item) => item.id === id);
    if (itemIndex !== -1) {
      this.items.splice(itemIndex, 1);
    }

    this.$nextTick(() => {
      this.$forceUpdate();
    });
  }

  reset = () => {
    this.items.splice(0, this.items.length);
    this.collapsed = false;
    this.nextId = 1;

    this.$nextTick(() => {
      this.$forceUpdate();
    });
  };

  handleResize = () => {
    const wasMobileViewport = this.windowWidth < TOTAL_PANEL_MOBILE_BREAKPOINT;
    this.updateViewportWidth();

    if (wasMobileViewport !== this.isMobileViewport) {
      this.setMobileVisible(false);
      this.setDefaultPosition();
    }
  };

  setMobileVisible(visible: boolean) {
    this.$emit("update:mobileVisible", visible);
  }

  updateViewportWidth() {
    this.windowWidth = window.innerWidth;
  }

  setDefaultPosition() {
    this.x = this.isMobileViewport ? 8 : 66;
    this.y = 65;
  }

  clearAll() {
    this.reset();
  }

  onClearAllPointerDown = (event: PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.clearAll();
  };

  endDragState() {
    const target = this.$el.querySelector(".stp-drag-handle") as HTMLElement | null;
    if (target && this.activePointerId !== null && target.hasPointerCapture(this.activePointerId)) {
      target.releasePointerCapture(this.activePointerId);
    }

    this.activePointerId = null;
    this.dragging = false;
  }

  toggleCollapse() {
    this.collapsed = !this.collapsed;
  }

  onTotalClick() {
    if (this.collapsed) {
      this.collapsed = false;
    }
  }

  formatValue(val: number): string {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val || 0);
  }

  startDrag(e: PointerEvent) {
    if (e.button !== 0) return;
    e.preventDefault();
    this.dragging = true;
    this.activePointerId = e.pointerId;
    this.dragOffsetX = e.clientX - this.x;
    this.dragOffsetY = e.clientY - this.y;
    // Capture pointer: all subsequent pointer events go to this element
    // even when the cursor moves outside it
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  onDragMove(e: PointerEvent) {
    if (!this.dragging) return;
    e.preventDefault();
    const panelWidth = (this.$refs.panel as HTMLElement)?.offsetWidth || 150;
    const panelHeight = (this.$refs.panel as HTMLElement)?.offsetHeight || 200;
    this.x = Math.max(
      0,
      Math.min(e.clientX - this.dragOffsetX, window.innerWidth - panelWidth)
    );
    this.y = Math.max(
      0,
      Math.min(e.clientY - this.dragOffsetY, window.innerHeight - panelHeight)
    );
  }

  onDragEnd(e?: PointerEvent) {
    const target = (e?.currentTarget as HTMLElement) || (this.$el.querySelector(".stp-header") as HTMLElement | null);
    if (target && this.activePointerId !== null && target.hasPointerCapture(this.activePointerId)) {
      target.releasePointerCapture(this.activePointerId);
    }

    this.endDragState();
  }
}
